// import React, { useState } from "react";
// import axios from "axios";

// const OpenCageAutocomplete = ({ value, onChange }) => {
//   const [suggestions, setSuggestions] = useState([]);

//   const handleInputChange = async (e) => {
//     const input = e.target.value;
//     onChange(e); // update formData

//     if (input.length < 3) {
//       setSuggestions([]);
//       return;
//     }

//     try {
//       const response = await axios.get(
//         `https://api.opencagedata.com/geocode/v1/json`,
//         {
//           params: {
//             key: "ebee1a9aeece4830abfb19e10dc1e21a",
//             q: input,
//             countrycode: "au",
//             limit: 5,
//             language: "en",
//           },
//         }
//       );

//       const results = response.data.results.map((item) => item.formatted);
//       setSuggestions(results);
//     } catch (error) {
//       console.error("Error fetching OpenCage suggestions:", error);
//     }
//   };

//   const handleSuggestionClick = (suggestion) => {
//     onChange({ target: { name: "address", value: suggestion } });
//     setSuggestions([]);
//   };

//   return (
//     <div className="position-relative">
//       <input
//         type="text"
//         className="form-control"
//         placeholder="Enter Address"
//         name="address"
//         value={value}
//         onChange={handleInputChange}
//         required
//         autoComplete="off"
//       />
//       {suggestions.length > 0 && (
//         <ul className="list-group position-absolute w-100" style={{ zIndex: 10 }}>
//           {suggestions.map((suggestion, index) => (
//             <li
//               key={index}
//               className="list-group-item list-group-item-action"
//               onClick={() => handleSuggestionClick(suggestion)}
//               style={{ cursor: "pointer" }}
//             >
//               {suggestion}
//             </li>
//           ))}
//         </ul>
//       )}
//     </div>
//   );
// };

// export default OpenCageAutocomplete;

import React, { useState, useEffect, useRef, useCallback } from "react";

const GooglePlacesAutocomplete = ({ 
  value = "", 
  onChange, 
  name = "address", 
  placeholder = "Enter Address",
  className = "form-control",
  required = false,
  country = "au", // Australia
  types = ["address"], // Can be ["address"], ["geocode"], ["establishment"], etc.
  onPlaceSelected = null, // Callback for when a place is selected with full details
  onError = null, // Error callback
  debounceMs = 300 // Debounce delay for manual input
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimer = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  // Check if Google Maps is available
  const checkGoogleMapsAvailability = useCallback(() => {
    return window.google && 
           window.google.maps && 
           window.google.maps.places &&
           window.google.maps.places.Autocomplete &&
           window.google.maps.places.AutocompleteService;
  }, []);

  // Wait for Google Maps to load
  useEffect(() => {
    const checkAndSetGoogleMaps = () => {
      if (checkGoogleMapsAvailability()) {
        setIsGoogleMapsLoaded(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkAndSetGoogleMaps()) return;

    // Poll for Google Maps with exponential backoff
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max
    
    const interval = setInterval(() => {
      attempts++;
      if (checkAndSetGoogleMaps()) {
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        setError("Google Maps failed to load. Please check your API key and internet connection.");
        if (onError) onError("Google Maps failed to load");
      }
    }, 100);

    return () => clearInterval(interval);
  }, [checkGoogleMapsAvailability, onError]);

  // Initialize Google Places services
  useEffect(() => {
    if (isGoogleMapsLoaded) {
      try {
        // Initialize AutocompleteService for manual suggestions
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        
        // Initialize PlacesService for place details
        const mapDiv = document.createElement('div');
        placesService.current = new window.google.maps.places.PlacesService(mapDiv);
        
      } catch (error) {
        console.error('Error initializing Places services:', error);
        setError("Failed to initialize Google Places services");
      }
    }
  }, [isGoogleMapsLoaded]);

  // Initialize Traditional Autocomplete Widget
  useEffect(() => {
    if (isGoogleMapsLoaded && inputRef.current && !autocompleteRef.current) {
      try {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          inputRef.current,
          {
            componentRestrictions: { country: country },
            fields: ['place_id', 'formatted_address', 'geometry', 'name', 'address_components'],
            types: types
          }
        );

        const placeChangedListener = autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          
          if (place && place.formatted_address) {
            // Create synthetic event
            const syntheticEvent = {
              target: {
                name: name,
                value: place.formatted_address
              }
            };
            
            onChange(syntheticEvent);
            
            // Clear manual suggestions
            setSuggestions([]);
            setShowSuggestions(false);
            
            // Call callback with full place details
            if (onPlaceSelected) {
              onPlaceSelected({
                address: place.formatted_address,
                placeId: place.place_id,
                coordinates: place.geometry?.location ? {
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng()
                } : null,
                addressComponents: place.address_components || [],
                fullPlace: place
              });
            }
          }
        });

        // Store listener reference for cleanup
        autocompleteRef.current._placeChangedListener = placeChangedListener;

      } catch (error) {
        console.error('Error initializing Traditional Autocomplete:', error);
        setError("Failed to initialize autocomplete widget");
      }
    }

    return () => {
      if (autocompleteRef.current && window.google?.maps?.event) {
        if (autocompleteRef.current._placeChangedListener) {
          window.google.maps.event.removeListener(autocompleteRef.current._placeChangedListener);
        }
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isGoogleMapsLoaded, country, types, name, onChange, onPlaceSelected]);

  // Manual autocomplete using AutocompleteService
  const fetchManualSuggestions = useCallback(async (input) => {
    if (!autocompleteService.current || input.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      autocompleteService.current.getPlacePredictions({
        input: input,
        componentRestrictions: { country: country },
        types: types
      }, (predictions, status) => {
        setIsLoading(false);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
          setShowSuggestions(true);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          setSuggestions([]);
          setShowSuggestions(false);
        } else {
          console.error('AutocompleteService error:', status);
          setError(`Places API error: ${status}`);
          setSuggestions([]);
          setShowSuggestions(false);
          if (onError) onError(status);
        }
      });
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setIsLoading(false);
      setError("Error fetching suggestions");
      if (onError) onError(error.message);
    }
  }, [country, types, onError]);

  // Handle input changes
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // Always call onChange to update parent state
    onChange(e);
    
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // If Google Maps widget is working, let it handle suggestions
    if (autocompleteRef.current) {
      // The traditional widget handles its own suggestions
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    // Otherwise, use manual suggestions as fallback
    if (inputValue.length >= 2) {
      debounceTimer.current = setTimeout(() => {
        fetchManualSuggestions(inputValue);
      }, debounceMs);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion click (for manual suggestions only)
  const handleSuggestionClick = async (prediction) => {
    // Update input value
    const syntheticEvent = {
      target: { name: name, value: prediction.description }
    };
    onChange(syntheticEvent);
    
    // Hide suggestions
    setSuggestions([]);
    setShowSuggestions(false);

    // Get detailed place information
    if (placesService.current && onPlaceSelected) {
      try {
        placesService.current.getDetails({
          placeId: prediction.place_id,
          fields: ['geometry', 'formatted_address', 'place_id', 'address_components', 'name']
        }, (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            onPlaceSelected({
              address: place.formatted_address,
              placeId: place.place_id,
              coordinates: place.geometry?.location ? {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              } : null,
              addressComponents: place.address_components || [],
              fullPlace: place
            });
          }
        });
      } catch (error) {
        console.error("Error fetching place details:", error);
      }
    }
  };

  // Handle key navigation
  const handleKeyDown = (e) => {
    // Prevent form submission if autocomplete dropdown is visible
    if (e.key === 'Enter') {
      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer && window.getComputedStyle(pacContainer).display !== 'none') {
        e.preventDefault();
      } else if (showSuggestions && suggestions.length > 0) {
        e.preventDefault();
      }
    }
    
    // Handle escape key
    if (e.key === 'Escape') {
      setSuggestions([]);
      setShowSuggestions(false);
      inputRef.current?.blur();
    }
  };

  // Hide suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update input value when prop changes
  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== value) {
      inputRef.current.value = value || '';
    }
  }, [value]);

  return (
    <div className="position-relative" ref={suggestionsRef}>
      <input
        ref={inputRef}
        type="text"
        className={className}
        placeholder={placeholder}
        name={name}
        defaultValue={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        required={required}
        autoComplete="off"
        style={{ boxSizing: 'border-box' }}
      />
      
      {/* Error display */}
      {error && (
        <div className="text-danger small mt-1">
          {error}
        </div>
      )}
      
      {/* Loading indicator for manual suggestions */}
      {isLoading && showSuggestions && (
        <div className="position-absolute w-100 mt-1 bg-white border rounded shadow" style={{ zIndex: 1050 }}>
          <div className="p-2 text-center text-muted">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Loading suggestions...
          </div>
        </div>
      )}
      
      {/* Manual suggestions dropdown (fallback when widget doesn't work) */}
      {showSuggestions && suggestions.length > 0 && !isLoading && (
        <ul className="list-group position-absolute w-100 mt-1 shadow" style={{ zIndex: 1050 }}>
          {suggestions.map((prediction) => (
            <li
              key={prediction.place_id}
              className="list-group-item list-group-item-action"
              onClick={() => handleSuggestionClick(prediction)}
              style={{ cursor: "pointer" }}
            >
              <div>
                <strong>{prediction.structured_formatting.main_text}</strong>
                {prediction.structured_formatting.secondary_text && (
                  <>
                    <br />
                    <small className="text-muted">
                      {prediction.structured_formatting.secondary_text}
                    </small>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      
      {/* Loading indicator when Google Maps is loading */}
      {!isGoogleMapsLoaded && (
        <div className="text-muted small mt-1">
          Loading Google Maps...
        </div>
      )}
    </div>
  );
};

// Usage Example Component
const AddressFormExample = () => {
  const [formData, setFormData] = useState({
    address: '',
    city: '',
    state: '',
    postcode: '',
    coordinates: null
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handlePlaceSelected = (placeDetails) => {
    console.log('Full place details:', placeDetails);
    
    // Parse address components
    let city = '', state = '', postcode = '';
    
    if (placeDetails.addressComponents) {
      placeDetails.addressComponents.forEach(component => {
        const types = component.types;
        
        if (types.includes('locality')) {
          city = component.long_name;
        } else if (types.includes('sublocality_level_1') && !city) {
          city = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
          state = component.short_name;
        } else if (types.includes('postal_code')) {
          postcode = component.long_name;
        }
      });
    }
    
    // Update form data with all extracted information
    setFormData(prev => ({
      ...prev,
      city: city,
      state: state,
      postcode: postcode,
      coordinates: placeDetails.coordinates
    }));
  };

  const handleError = (error) => {
    console.error('Places API Error:', error);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert(`Form Data:\n${JSON.stringify(formData, null, 2)}`);
  };

  return (
    <div className="container mt-4">
      <h3>Address Form Example</h3>
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-8 mb-3">
            <label className="form-label">Address *</label>
            <GooglePlacesAutocomplete
              value={formData.address}
              onChange={handleChange}
              onPlaceSelected={handlePlaceSelected}
              onError={handleError}
              name="address"
              placeholder="Enter your address"
              required={true}
              country="au"
              types={["address"]}
            />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Postcode</label>
            <input
              type="text"
              className="form-control"
              name="postcode"
              value={formData.postcode}
              readOnly
              placeholder="Auto-filled"
            />
          </div>
        </div>
        
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">City/Suburb</label>
            <input
              type="text"
              className="form-control"
              name="city"
              value={formData.city}
              readOnly
              placeholder="Auto-filled"
            />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">State</label>
            <input
              type="text"
              className="form-control"
              name="state"
              value={formData.state}
              readOnly
              placeholder="Auto-filled"
            />
          </div>
        </div>
        
        <div className="mb-3">
          <button type="submit" className="btn btn-primary">
            Submit Address
          </button>
        </div>
        
        <div className="mt-4">
          <h5>Current Form Data:</h5>
          <pre className="bg-light p-3 rounded">
            {JSON.stringify(formData, null, 2)}
          </pre>
        </div>
      </form>
    </div>
  );
};

// Export both the component and example
export default GooglePlacesAutocomplete;
export { AddressFormExample };

// Additional utility functions you can use

// Extract specific address components
export const parseAddressComponents = (addressComponents) => {
  const result = {
    streetNumber: '',
    streetName: '',
    suburb: '',
    city: '',
    state: '',
    postcode: '',
    country: ''
  };

  if (!addressComponents) return result;

  addressComponents.forEach(component => {
    const types = component.types;
    
    if (types.includes('street_number')) {
      result.streetNumber = component.long_name;
    } else if (types.includes('route')) {
      result.streetName = component.long_name;
    } else if (types.includes('sublocality_level_1')) {
      result.suburb = component.long_name;
    } else if (types.includes('locality')) {
      result.city = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      result.state = component.short_name;
    } else if (types.includes('postal_code')) {
      result.postcode = component.long_name;
    } else if (types.includes('country')) {
      result.country = component.short_name;
    }
  });

  return result;
};

// Validate Australian postcode
export const validateAustralianPostcode = (postcode) => {
  const postcodeRegex = /^[0-9]{4}$/;
  return postcodeRegex.test(postcode);
};

// Format address for display
export const formatDisplayAddress = (addressComponents) => {
  const parsed = parseAddressComponents(addressComponents);
  const parts = [
    parsed.streetNumber,
    parsed.streetName,
    parsed.suburb,
    parsed.city,
    parsed.state,
    parsed.postcode
  ].filter(part => part.trim() !== '');
  
  return parts.join(', ');
};